import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { FileText, Download, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TEMPLATES = [
  "Offer Letter", "Appointment Letter", "Salary Revision", "Experience Letter",
  "Relieving Letter", "Educational Certificate", "Payslip", "Policy Document",
];

export default function Documents() {
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const load = () => api.get("/files").then((r) => setFiles(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/files/upload?category=document`, fd);
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    toast.success("Deleted");
    load();
  };

  const open = (id) => {
    const token = localStorage.getItem("hr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${id}/download?auth=${token}`, "_blank");
  };

  return (
    <div data-testid="documents-page">
      <PageHeader
        title="Documents"
        subtitle="Templates, employee docs, payslips, and policy library."
        action={
          <label className="inline-flex items-center h-9 px-3 rounded-md bg-[#F26B3A] hover:bg-[#d85d2f] text-white text-sm cursor-pointer" data-testid="upload-doc-label">
            <Upload className="h-4 w-4 mr-1.5" /> Upload document
            <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid="upload-doc-input" />
          </label>
        }
      />

      <h2 className="font-display text-lg font-semibold text-[#0F172A] mb-3">My uploads</h2>
      <Card className="hr-card mb-8 divide-y divide-gray-100" data-testid="my-files">
        {files.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No files uploaded yet.</div>
        ) : files.map((f) => (
          <div key={f.id} className="p-4 flex items-center gap-3 hover:bg-gray-50">
            <div className="h-9 w-9 rounded-lg bg-[#F26B3A]/5 text-[#0F172A] grid place-items-center">
              <FileText className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{f.original_filename}</div>
              <div className="text-[11px] text-gray-500">{(f.size / 1024).toFixed(1)} KB · {f.category} · {new Date(f.created_at).toLocaleDateString()}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => open(f.id)} data-testid={`open-${f.id}`}>
              <Download className="h-3.5 w-3.5 mr-1" /> View
            </Button>
            <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-red-600 hover:text-red-700" data-testid={`del-${f.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </Card>

      <h2 className="font-display text-lg font-semibold text-[#0F172A] mb-3">Templates</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TEMPLATES.map((t) => (
          <Card key={t} className="hr-card p-5 flex flex-col gap-3" data-testid={`doc-${t.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="h-10 w-10 rounded-lg bg-[#F26B3A]/5 text-[#0F172A] grid place-items-center">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <div className="font-medium text-sm">{t}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Template · v2.1</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
